import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#0a192f',
        color: '#e6f1ff',
        py: isMobile ? 4 : 6,
        borderTop: '1px solid rgba(100, 255, 218, 0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={isMobile ? 3 : 5}
          sx={{ 
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}
        >
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                mb: isMobile ? 2 : 0
              }}
            >
              <Box
                component="a"
                href="#home"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  mr: 2,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  textDecoration: "none",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 750,
                    color: theme.palette.secondary.main,
                    lineHeight: 1,
                    letterSpacing: "1px",
                    fontSize: isMobile ? "1.1rem" : "1.25rem",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
                    transition: "all 0.3s ease",
                  }}
                >
                  SAINI
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.secondary.main,
                    letterSpacing: "4px",
                    textTransform: "uppercase",
                    mt: "-2px",
                    fontSize: isMobile ? "0.5rem" : "0.7rem",
                    transition: "all 0.3s ease",
                    opacity: 0.8,
                  }}
                >
                  COLLECTION
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: '#8892b0',
                  lineHeight: 1.7,
                  mb: 2,
                  mt: 2,
                  fontSize: isMobile ? '0.85rem' : '0.95rem',
                }}
              >
                Driving digital growth with creativity, technology, and care — since 2025.
              </Typography>
              <Box sx={{
                display: 'flex',
                gap: 2,
                mt: 1,
                flexWrap: 'wrap'
              }}>
                <Typography variant="body2" sx={{
                  backgroundColor: 'rgba(100, 255, 218, 0.1)',
                  color: theme.palette.secondary.main,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '4px',
                  fontWeight: 500,
                  fontSize: isMobile ? '0.75rem' : '0.85rem'
                }}>
                  Web Design
                </Typography>
                <Typography variant="body2" sx={{
                  backgroundColor: 'rgba(100, 255, 218, 0.1)',
                  color: theme.palette.secondary.main,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '4px',
                  fontWeight: 500,
                  fontSize: isMobile ? '0.75rem' : '0.85rem'
                }}>
                  Development
                </Typography>
                <Typography variant="body2" sx={{
                  backgroundColor: 'rgba(100, 255, 218, 0.1)',
                  color: theme.palette.secondary.main,
                  px:1,
                  py: 0.5,
                  borderRadius: '4px',
                  fontWeight: 500,
                  fontSize: isMobile ? '0.75rem' : '0.85rem'
                }}>
                  Digital Solutions
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2} sx={{ 
            mt: isMobile ? 2 : 0,
            order: isMobile ? 2 : 'initial' 
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                color: '#ccd6f6',
                fontWeight: 600,
                position: 'relative',
                pb: 1,
                fontSize: isMobile ? '0.95rem' : '1rem',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '2px',
                  background: theme.palette.secondary.main
                }
              }}
            >
              Company
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 3.5,
              pl: isMobile ? 0 : 0
            }}>
              {[
                { label: 'About Us', href: '#about' },
                { label: 'Services', href: '#services' },
                { label: 'Testimonials', href: '#testimonials' },
                { label: 'Contact', href: '#contact' },
              ].map((item, index) => (
                <Link
                  key={index}
                  href={item.href}
                  underline="none"
                  sx={{
                    color: '#8892b0',
                    fontSize: isMobile ? '0.85rem' : '0.95rem',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      color: theme.palette.secondary.main,
                      pl: '8px',
                    },
                    '&::before': {
                      content: '"»"',
                      marginRight: '8px',
                      opacity: 0,
                      transition: 'all 0.3s ease',
                      color: theme.palette.secondary.main
                    },
                    '&:hover::before': {
                      opacity: 1
                    }
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={12} md={5} sx={{ 
            mt: isMobile ? 2 : 0,
            order: isMobile ? 1 : 'initial' 
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 2,
                color: '#ccd6f6',
                fontWeight: 600,
                position: 'relative',
                pb: 1,
                fontSize: isMobile ? '0.95rem' : '1rem',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '2px',
                  background: theme.palette.secondary.main
                }
              }}
            >
              Contact
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2.5,
              flexWrap: 'wrap'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1.5,
                flexWrap: 'wrap'
              }}>
                <Box sx={{
                  backgroundColor: 'rgba(100, 255, 218, 0.1)',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Email
                    sx={{ 
                      color: theme.palette.secondary.main, 
                      fontSize: '1.1rem' 
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#ccd6f6',
                      fontWeight: 500,
                      mb: 0.5,
                      fontSize: isMobile ? '0.85rem' : '0.95rem'
                    }}
                  >
                    Email
                  </Typography>
                  <Link
                    href="mailto:info@sainicollection.com"
                    underline="none"
                    sx={{
                      color: '#8892b0',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: theme.palette.secondary.main,
                      }
                    }}
                  >
                    info@sainicollection.com
                  </Link>
                </Box>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1.5,
                flexWrap: 'wrap'
              }}>
                <Box sx={{
                  backgroundColor: 'rgba(100, 255, 218, 0.1)',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Phone
                    sx={{ 
                      color: theme.palette.secondary.main, 
                      fontSize: '1.1rem' 
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#ccd6f6',
                      fontWeight: 500,
                      mb: 0.5,
                      fontSize: isMobile ? '0.85rem' : '0.95rem'
                    }}
                  >
                    Phone
                  </Typography>
                  <Link
                    href="tel:+918700274294"
                    underline="none"
                    sx={{
                      color: '#8892b0',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: theme.palette.secondary.main,
                      }
                    }}
                  >
                    +91 8700274294
                  </Link>
                </Box>
              </Box>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: 1.5,
                flexWrap: 'wrap'
              }}>
                <Box sx={{
                  backgroundColor: 'rgba(100, 255, 218, 0.1)',
                  borderRadius: '50%',
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <LocationOn
                    sx={{ 
                      color: theme.palette.secondary.main, 
                      fontSize: '1.1rem' 
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#ccd6f6',
                      fontWeight: 500,
                      mb: 0.5,
                      fontSize: isMobile ? '0.85rem' : '0.95rem'
                    }}
                  >
                    Location
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#8892b0',
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                    }}
                  >
                    Gurgaon (Haryana), India
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{
          borderColor: 'rgba(136, 146, 176, 0.2)',
          my: isMobile ? 3 : 5
        }} />

        {/* Bottom Line */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#495670',
              fontSize: isMobile ? '0.75rem' : '0.9rem',
              lineHeight: 1.5
            }}
          >
            &copy; {new Date().getFullYear()} SAINI COLLECTION. All rights reserved.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#495670',
              fontSize: isMobile ? '0.75rem' : '0.9rem',
              lineHeight: 1.5
            }}
          >
            Designed and Developed with ❤️ in India
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;